import { ActionIcon, Button, CloseButton, Group, Menu, Modal, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { useAtom } from 'jotai';
import { useState } from 'react';

import { successNotification } from 'shared/notifications';

import { useCatalogLabels } from '../labels';
import {
    MAX_SEARCH_PROFILES,
    newProfileId,
    searchProfilesAtom,
    searchStateFromQuery,
    shareUrl,
    toSearchParams,
    useCatalogSearchState,
    type CatalogSearchState
} from '../searchShare';

interface SearchShareActionsProps {
    /** Pre-filled name in the "save" dialog - the caller knows the node's label and filter count. */
    defaultProfileName: string;
    /** Applied to a profile before it is restored (see searchShare.sanitizeSearchState): the data
     * may have changed since it was saved. */
    sanitize: (state: CatalogSearchState) => CatalogSearchState;
}

const isDocumentProfile = (query: string) => searchStateFromQuery(query).mode === 'documents';

const icon = (name: string, size = 18) => (
    <span className="material-symbols-outlined" style={{ fontSize: size }} aria-hidden>
        {name}
    </span>
);

/** The two toolbar icons: dark blue glyph, grey wash on hover (App.css), name in a tooltip. */
const ICON_BUTTON_PROPS = { size: 'lg', variant: 'subtle', radius: 'sm' } as const;

/** The profiles menu in the app's three colours: white panel, thin grey frame, dark text, grey
 * hover - the default menu under @uag/doka-design looked off (reported: "Farben für den
 * Suchprofil-Dialog sehen auch nicht gut aus"). */
const MENU_STYLES = {
    dropdown: { border: '1px solid var(--pih-border)', borderRadius: 6, padding: 6, background: '#fff' },
    label: {
        color: 'var(--pih-muted)',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase'
    },
    item: { color: 'var(--pih-text)', borderRadius: 4 },
    divider: { borderColor: 'var(--pih-border)' }
} as const;

/**
 * "Link teilen" and "Suchprofile" (reported: "Können wir einen 'Link teilen' einbauen der alle
 * Suchparameter in der url speichert? Idee: Suchprofil speichern im Browser"). Two icons at the
 * right end of the toolbar's Produkte/Dokumente row - icons only, on every width (reported: "Für
 * 'Link teilen' und 'Suchprofile' nehmen wir nur die Icons"); the name is in the tooltip and the
 * accessible label.
 *
 * Both are built on the same snapshot (searchShare.ts): the link carries it as URL parameters, a
 * profile stores those very parameters under a name in this browser.
 */
export const SearchShareActions = ({ defaultProfileName, sanitize }: SearchShareActionsProps) => {
    const { L } = useCatalogLabels();
    const { current, apply } = useCatalogSearchState();
    const [profiles, setProfiles] = useAtom(searchProfilesAtom);
    const [fallbackUrl, setFallbackUrl] = useState<string>();
    const [saveOpen, setSaveOpen] = useState(false);
    const [profileName, setProfileName] = useState('');

    const handleShare = async () => {
        const url = shareUrl(current);
        // A phone gets its own share sheet (mail, messenger, ...); a desktop browser's share
        // dialog, where one exists, is no improvement over the clipboard.
        const touch = window.matchMedia?.('(pointer: coarse)').matches;
        if (touch && typeof navigator.share === 'function') {
            try {
                await navigator.share({ title: L.title, url });
                return;
            } catch (error) {
                if ((error as Error).name === 'AbortError') {
                    return;
                }
            }
        }
        try {
            await navigator.clipboard.writeText(url);
            successNotification({ message: L.shareLinkCopied });
        } catch {
            // No clipboard access (insecure context, denied permission): show the link to copy by hand.
            setFallbackUrl(url);
        }
    };

    const handleOpenSave = () => {
        setProfileName(defaultProfileName);
        setSaveOpen(true);
    };

    const handleSave = () => {
        const name = profileName.trim();
        if (!name) {
            return;
        }
        const profile = {
            id: newProfileId(),
            name,
            savedAt: new Date().toISOString(),
            query: toSearchParams(current).toString()
        };
        // Same name again means "update that one", not a second entry nobody can tell apart.
        setProfiles((prev) =>
            [profile, ...prev.filter((p) => p.name.toLocaleLowerCase() !== name.toLocaleLowerCase())].slice(
                0,
                MAX_SEARCH_PROFILES
            )
        );
        setSaveOpen(false);
        successNotification({ message: L.searchProfileSaved.replace('{name}', name) });
    };

    const handleApply = (query: string) => apply(sanitize(searchStateFromQuery(query)));

    return (
        <Group gap={4} wrap="nowrap">
            <Tooltip label={L.shareLinkHint} openDelay={300} withArrow>
                <ActionIcon {...ICON_BUTTON_PROPS} aria-label={L.shareLink} onClick={() => void handleShare()}>
                    {icon('share', 20)}
                </ActionIcon>
            </Tooltip>

            <Menu position="bottom-end" shadow="sm" styles={MENU_STYLES} width={340} withinPortal>
                {/* Tooltip OUTSIDE Menu.Target: Menu.Target hands its click handler and ref down
                    to its direct child, which has to be the button itself. */}
                <Tooltip label={L.searchProfiles} openDelay={300} withArrow>
                    <Menu.Target>
                        <ActionIcon {...ICON_BUTTON_PROPS} aria-label={L.searchProfiles}>
                            {icon('saved_search', 20)}
                        </ActionIcon>
                    </Menu.Target>
                </Tooltip>
                <Menu.Dropdown>
                    <Menu.Item
                        fw={600}
                        leftSection={icon('bookmark_add')}
                        style={{ color: 'var(--pih-primary)' }}
                        onClick={handleOpenSave}
                    >
                        {L.saveSearchProfile}
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Label>{L.savedSearchProfiles}</Menu.Label>
                    {profiles.length === 0 ? (
                        <Text c="dimmed" px="sm" py={6} size="sm">
                            {L.noSearchProfiles}
                        </Text>
                    ) : (
                        profiles.map((profile) => (
                            // A div, not the default button: it holds the delete button, and a
                            // button inside a button is invalid HTML.
                            <Menu.Item
                                key={profile.id}
                                component="div"
                                // Product or document search, at a glance - the same profile list
                                // holds both (the mode is part of the saved state).
                                leftSection={
                                    <span
                                        aria-label={
                                            isDocumentProfile(profile.query)
                                                ? L.searchModeDocuments
                                                : L.searchModeProducts
                                        }
                                        className="material-symbols-outlined"
                                        role="img"
                                        style={{ fontSize: 18, color: 'var(--pih-muted)' }}
                                        title={
                                            isDocumentProfile(profile.query)
                                                ? L.searchModeDocuments
                                                : L.searchModeProducts
                                        }
                                    >
                                        {isDocumentProfile(profile.query) ? 'description' : 'inventory_2'}
                                    </span>
                                }
                                rightSection={
                                    <CloseButton
                                        aria-label={`${L.deleteSearchProfile}: ${profile.name}`}
                                        size="sm"
                                        title={L.deleteSearchProfile}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setProfiles((prev) => prev.filter((p) => p.id !== profile.id));
                                        }}
                                    />
                                }
                                style={{ cursor: 'pointer' }}
                                // min-width 0: without it the label grows with a long name and
                                // pushes the delete button out of the menu instead of truncating.
                                styles={{ itemLabel: { minWidth: 0 } }}
                                title={profile.name}
                                onClick={() => handleApply(profile.query)}
                            >
                                <Text size="sm" truncate>
                                    {profile.name}
                                </Text>
                            </Menu.Item>
                        ))
                    )}
                    <Text c="dimmed" pb={6} pt={4} px="sm" size="xs">
                        {L.searchProfilesLocalHint}
                    </Text>
                </Menu.Dropdown>
            </Menu>

            <Modal opened={saveOpen} title={L.saveSearchProfile} centered onClose={() => setSaveOpen(false)}>
                <form
                    onSubmit={(event) => {
                        event.preventDefault();
                        handleSave();
                    }}
                >
                    <Stack gap="md">
                        <TextInput
                            label={L.searchProfileName}
                            value={profileName}
                            data-autofocus
                            onChange={(event) => setProfileName(event.currentTarget.value)}
                        />
                        <Group gap="xs" justify="flex-end">
                            <Button variant="default" onClick={() => setSaveOpen(false)}>
                                {L.cancel}
                            </Button>
                            <Button disabled={!profileName.trim()} type="submit">
                                {L.save}
                            </Button>
                        </Group>
                    </Stack>
                </form>
            </Modal>

            <Modal
                opened={fallbackUrl !== undefined}
                title={L.shareLink}
                centered
                onClose={() => setFallbackUrl(undefined)}
            >
                <Stack gap="xs">
                    <Text size="sm">{L.shareLinkManualCopy}</Text>
                    <TextInput value={fallbackUrl ?? ''} readOnly onFocus={(event) => event.currentTarget.select()} />
                </Stack>
            </Modal>
        </Group>
    );
};
